import { Component, effect, input, Input, model, OnChanges, OnDestroy, output, signal, SimpleChanges } from "@angular/core";
import { ListingComponentInterface } from "../interfaces/listing-component.interface";
import { ListingConfigInterface } from "../interfaces/listing-config.interface";
import { PaginationChangesInterface, PaginationComponentInterface } from "../../interfaces/pagination.interface";
import { FilterChangesInterface, FilterComponentInterface, FilterDataInterface } from "../../interfaces/filters.interface";
import { SearchChangesInterface, SearchComponentInterface } from "../../interfaces/search.interface";
import { Observable, Subject, Subscription, timer } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { debounceTime, switchMap, takeUntil } from "rxjs/operators";
import { FiltersGraphqlInput, ListingGraphqlInput, PaginationGraphqlInput, SearchGraphqlInput, SortGraphqlInput } from "@kernel/graphql/models";
import { ApolloQueryResult } from "@apollo/client/core";
import { HttpHeaders } from "@angular/common/http";
import { FilterConditionEnum } from "../../filter/enums/filter-condition.enum";
import { AuthService } from "@kernel/tools/services/auth.service";
import { outputToObservable } from "@angular/core/rxjs-interop";
import { ListActionInterface } from "@kernel/listing/interfaces/listing-items-actions.interface";
import { SearchValueInterface } from "@kernel/listing/search-input/interfaces/search-value";
import { ISortData } from "@kernel/listing/interfaces/sort.interface";
import { mergeDeep } from "@app/kernel/shared/helpers";

@Component({
  template: "",
})
export abstract class BaseListing implements ListingComponentInterface, OnDestroy, OnChanges {
  @Input() config!: ListingConfigInterface;
  @Input() data: any[] = [];
  @Input() staticData: any[] | undefined = undefined;

  /** @deprecated Use `paginationValue` while manually handling `pageChanged` `totalResultsChanged` events  instead. */
  @Input() pagination: PaginationComponentInterface | undefined;
  /** @deprecated Use `filtersValue` instead. */
  @Input() filter: FilterComponentInterface | undefined;
  /** @deprecated Use `searchValue` instead. */
  @Input() search: SearchComponentInterface | undefined;
  /** @deprecated Use `filtersValue` instead. */
  @Input() filterOption?: FilterConditionEnum;

  paginationValue = input<PaginationChangesInterface>();
  filtersValue = input<FilterChangesInterface>();
  searchValue = input<SearchChangesInterface>();

  pageChanged = output<number>();
  totalResultsChanged = output<number>();

  @Input() theme = "sxw-theme";
  @Input() paramsDef!: Record<string, string>;
  @Input() paramsData!: Record<string, any>;
  @Input() filterData?: FilterDataInterface[];
  @Input() sortData?: ISortData;
  @Input() searchData?: SearchValueInterface;
  @Input() groupActions = 2;

  @Input()
  get customPaginationData(): PaginationChangesInterface {
    return this._paginationData;
  }
  set customPaginationData(value: PaginationChangesInterface | undefined) {
    if (!value) return;
    this._paginationData = value;
  }

  tableSelectedHeader = input<string>();
  listingGraphqlQuery = signal<ListingGraphqlInput | undefined | null>(undefined);
  listingGraphqlQueryChange = output<ListingGraphqlInput | null | undefined>();

  selectedData = model<any[]>([]);
  disabledSelection = input<boolean>(false);

  protected _data: any[] = [];
  protected _loading = true;
  protected _errored: any;
  protected _sort: any = {};
  protected _queryFields: string[] | undefined;

  /* Start Integrations { */

  protected _paginationSubscription: Subscription | undefined;
  protected _filtersSubscription: Subscription | undefined;
  protected _searchSubscription: Subscription | undefined;

  protected _paginationData: PaginationChangesInterface = { page: 1, rpp: 20 };
  protected _filterData?: FilterChangesInterface;
  protected _searchData?: SearchChangesInterface;

  onSort = output<ISortData>();

  /* } End Integrations  */

  protected _unsubscribe = new Subject<void>();
  protected _subscription: Subscription | undefined;

  //*********************************************************************************//
  //******************************* Lifecycle Methods *******************************//
  //*********************************************************************************//
  constructor(
    protected graphService: GraphService,
    protected authService?: AuthService
  ) {
    effect(() => this.updateFilters(this.filtersValue()));
    effect(() => this.updateSearch(this.searchValue()));
    effect(() => this.updatePagination(this.paginationValue()));
  }

  /**
   * Angular lifecycle hook.
   * @param changes
   */
  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  /**
   * Angular lifecycle hook.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["data"]?.currentValue?.length && changes["config"] && typeof this.config.query === "string") {
      this.executeQuery();

      // Initialize default sort if provided and no existing sort
      if (this.config.defaultSort && !this._sort.field && !this._sort.order) {
        this._sort = {
          field: this.config.defaultSort.field,
          order: this.config.defaultSort.order,
        };
      }
    }
    if (changes["config"] || changes["data"]) {
      this.processChanges();
    }
    if (changes["data"]) {
      this.loading(false);
    }
    if (changes["pagination"]) {
      this.handlePaginationComponentChanges();
    }
    if (changes["filter"]) {
      this.handleFilterComponentChanges();
    }
    if (changes["search"]) {
      this.handleSearchComponentChanges();
    }
    if (changes["searchData"]) {
      this._searchData = changes["searchData"].currentValue;
    }
    if (changes["sortData"]) {
      this.sortList(changes["sortData"].currentValue);
    }
  }
  //***********************************************************************************//
  //******************************* Integration Methods *******************************//
  //***********************************************************************************//

  /** @deprecated will be removed and replaced with the reactive way. */
  protected handlePaginationComponentChanges() {
    this._paginationSubscription?.unsubscribe();
    if (!this.pagination) return;
    this._paginationSubscription = outputToObservable(this.pagination?.onPaginationChange)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((changes: PaginationChangesInterface) => this.updatePagination(changes));
  }

  /** @deprecated will be removed and replaced with the reactive way. */
  protected handleFilterComponentChanges() {
    this._filtersSubscription?.unsubscribe();
    if (this.filter?.onFilterApplied) {
      this._filtersSubscription = outputToObservable(this.filter?.onFilterApplied)
        .pipe(debounceTime(250), takeUntil(this._unsubscribe))
        .subscribe((changes: FilterChangesInterface) => this.updateFilters(changes));
    }
  }

  /** @deprecated will be removed and replaced with the reactive way. */
  protected handleSearchComponentChanges() {
    this._searchSubscription?.unsubscribe();
    if (this.search?.onSearchApplied) {
      this._searchSubscription = outputToObservable(this.search.onSearchApplied)
        .pipe(debounceTime(250), takeUntil(this._unsubscribe))
        .subscribe((changes: SearchChangesInterface) => this.updateSearch(changes));
    }
  }

  updatePagination(paginationValue?: PaginationChangesInterface) {
    this.customPaginationData = paginationValue;
    this.executeQuery();
  }

  updateFilters(filtersValue?: FilterChangesInterface) {
    this.customPaginationData.page = 1;
    this.changePage(1, false);
    this._filterData = filtersValue;
    this.executeQuery(true, true);
  }

  updateSearch(searchValue?: SearchChangesInterface) {
    this.customPaginationData.page = 1;
    this.changePage(1, false);
    this._searchData = searchValue;
    this.executeQuery(true, true);
  }

  changePage(page: number, emit = true) {
    // Changing hte pagination using the pagination component should be removed and replaced with a reactive way,
    // but until then we will keep this method to avoid breaking changes.
    // The emit value as well because it is no longer needed in the new way of handling pagination
    this.pagination?.changePage(page, emit);
    this.pageChanged.emit(page);
  }

  changeTotalResults(total: number) {
    // Changing hte pagination using the pagination component should be removed and replaced with a reactive way,
    // but until then we will keep this method to avoid breaking changes.
    this.pagination?.setTotal(total);
    this.totalResultsChanged.emit(total);
  }

  //*******************************************************************************//
  //******************************* Network Methods *******************************//
  //*******************************************************************************//

  /**
   * Returns an array of field names.
   * @protected
   */
  protected extractQueryFields(): string[] {
    let query: any[] = [];
    this.config.cols.forEach(col => {
      query = query.concat(Array.isArray(col.field) ? col.field : [col.field]);
    });
    return query.concat(this.config.queryFields ?? []).filter((item: string) => item);
  }

  /**
   * Returns the query fields, or extracts them if they were un extracted.
   * @protected
   */
  protected getQueryFields() {
    if (!this._queryFields) {
      this._queryFields = this.extractQueryFields();
    } // extract the query from table config.
    return this._queryFields;
  }

  /**
   * Triggers sort event.
   * @param event
   */
  protected sortList(event?: ISortData) {
    if (!event) {
      if (!!this._sort && !!Object.values(this._sort).length) {
        this._sort = {};
        this.reload();
      }
    } else {
      if (this._sort.field === event.field && this._sort.order === event.order) {
        return;
      }
      this._sort = { field: event.field, order: event.order };
      // trigger the event.
      this.onSort.emit({ field: event.field, order: event.order });
      if (!this.config.query) {
        return;
      } // if the list do not know the query stop executing.
      this.reload();
    }
  }

  /**
   * Returns additional params and paramsDef
   * @private
   * @return params passed from the table config and the Input after sanitizing and applying hierarchy
   */
  protected getAdditionalParams(): { params: any; paramsDef: any } | null {
    return { params: this.paramsData ?? this.config.paramsData, paramsDef: this.paramsDef ?? this.config.paramsDef };
  }

  /**
   * Returns the additional filters after removing the not valid filters
   */
  protected getAdditionalFilters(): FilterDataInterface[] | undefined {
    if (!this.filterData && !this.config.filterData) {
      return;
    }
    return (this.filterData! ?? this.config.filterData!).filter(
      filter => filter?.field && filter?.value !== null && filter?.value !== undefined && filter?.option
    );
  }

  /**
   * Returns the full filters input.
   * @protected
   */
  protected getFilters(): FilterDataInterface[] | undefined {
    const additional = this.getAdditionalFilters();
    if (!this._filterData && !additional) {
      return;
    }
    return (this._filterData?.filters ?? []).concat(additional ?? []);
  }

  /**
   * Creates and returns an Observable of ApolloQueryResult, representing a GraphQL query.
   * @protected
   */
  protected createQuery(loading = true): Observable<ApolloQueryResult<any>> {
    this.listingGraphqlQuery.set(this.getListingGraphqlInput());
    // get the additional params and add them to the listing query.
    const additionalParams = this.getAdditionalParams();
    if (additionalParams) {
      this.listingGraphqlQuery()?.addParams(additionalParams.params, additionalParams.paramsDef);
    }

    this.listingGraphqlQueryChange.emit(this.listingGraphqlQuery());
    // use graphql service to construct the query.
    return this.graphService.constructWatchListQuery(
      this.getQueryFields(),
      this.config.query,
      this.listingGraphqlQuery(),
      loading ? undefined : new HttpHeaders().set("ignore_http_loader", "ignore_http_loader")
    );
  }

  /**
   * Constructs and returns a GraphQL input object for listing queries,
   * incorporating filters, pagination, sorting, and search parameters.
   * @protected
   */
  protected getListingGraphqlInput() {
    return ListingGraphqlInput.wrap(
      new FiltersGraphqlInput(this.getFilters(), this._filterData?.condition ?? this.filterOption), // add filters to the query.
      new PaginationGraphqlInput(this.customPaginationData.page ?? 1, this.customPaginationData.rpp ?? 20), // add pagination to the query
      new SortGraphqlInput(this._sort.field, this._sort.order), // add sorting to the query.
      new SearchGraphqlInput(this._searchData?.fields, this._searchData?.value) // add search to the query
    );
  }

  /**
   * Executes a GraphQL query to fetch data for the listing component.
   * This function triggers loading indicators, manages query subscriptions,
   * and handles query responses and errors appropriately.
   * @protected
   */
  protected executeQuery(loading = true, forceDataRest = false): void {
    // check if the table is already loading the data.
    this.loading(loading);
    this._subscription?.unsubscribe();
    this._subscription = timer(250)
      .pipe(
        switchMap(() => this.createQuery(loading)),
        takeUntil(this._unsubscribe)
      )
      .subscribe({
        next: (data: any) => this.handleQueryResponse(data, forceDataRest),
        error: (error: any) => this.handleQueryError(error),
      });
  }

  //******************************************************************************//
  //******************************* Common List Methods *******************************//
  //******************************************************************************//

  protected abstract processDataChanges(): void;

  /**
   * When Query data is fetched successfully
   * @param data
   * @param forceDataRest
   * @protected
   */
  protected abstract handleQueryResponse(data: any, forceDataRest: boolean): void;

  /**
   * When Query fails.
   * @param _error
   * @protected
   */
  protected handleQueryError(_error: any) {
    this._errored = true;
    this._data = [];
    this.loading(false);
  }

  /**
   * Filters actions col to permissions available
   * @protected
   * @param actions
   */
  protected sanitizeActions(actions: ListActionInterface[] | undefined): ListActionInterface[] | undefined {
    if (!actions) {
      return actions;
    }
    return actions
      .filter(action => this.authService?.getUser()?.can(action.permissions as string))
      .map(a => (a.actions ? { ...a, actions: this.sanitizeActions(a.actions) } : a));
  }

  /**
   * Generates a unique col id
   */
  protected generateId(): string {
    return `COL${Math.random().toString(36).substring(2, 9)}`;
  }
  /**
   * Returns the row background color.
   * @param row
   */
  getRowBackground(row: any): string {
    if (typeof this.config.background === "function") {
      return this.config.background(row) || "";
    }
    return "";
  }

  /**
   * Returns the row classes list
   * @param row
   */
  getRowClass(row: any): { [key: string]: true } | {} {
    let _classes = typeof this.config.klass === "function" ? this.config.klass(row) : null;
    if (!_classes) {
      return {};
    }
    // the config.klass function might return a string or an array of strings, representing a single or multiple classes
    // to simplify dealing with it, we will ensure that the value is an array of strings
    if (!Array.isArray(_classes)) {
      _classes = [_classes];
    }
    // return an object with keys corresponding to each class in the _classes array, and values set to `true`
    return _classes.reduce((obj, _class) => ({ ...obj, [_class]: true }), {});
  }

  /**
   * Prepares both config and data to be used in the view
   */
  protected processChanges() {
    this._queryFields = undefined;
    this.config.cols?.forEach(col => {
      if (!col.id) {
        col.id = this.generateId();
      }
      if (Object.hasOwn(col, "actions")) {
        col.actions = this.sanitizeActions(col.actions);
      }
    });
    if (typeof this.config.fixedWidth === "boolean" || this.config.fixedWidth === "even") {
      this.config.fixedWidth = `${100 / this.config.cols.length}%`;
    }
    this.processDataChanges();
  }

  protected filterRows(data: any[]) {
    const _filterRows = this.config.filterRows;
    if (!_filterRows) return data;

    return data.filter(item => {
      return !!_filterRows(item);
    });
  }

  //******************************************************************************//
  //******************************* Public Methods *******************************//
  //******************************************************************************//

  abstract addItemClass(...args: any[]): void;

  abstract loading(state: boolean): void;

  abstract reRender(): void;

  abstract reload(): void;

  abstract removeItem(index: number, groupIndex?: number): void;

  abstract removeItemClass(index: number, _class: string, groupIndex?: number): void;

  abstract setDate(data: any[]): void;

  abstract setItemValue(index: number | Record<string, unknown>, key: string, value: any, groupIndex?: number): void;

  setItem(index?: number, value?: unknown, groupIndex?: number) {
    if (groupIndex !== undefined && index !== undefined) {
      if (value && typeof value === "object") {
        this.data[groupIndex].data[index] = mergeDeep({}, this.data[groupIndex].data[index], value);
      }
    } else if (index !== undefined) {
      if (typeof value === "object") {
        this.data[index] = mergeDeep({}, this.data[index], value);
      }
    } else return;
    this.processDataChanges();
  }

  findItemIndex(predicate: (item: any) => boolean): number {
    return this._data.findIndex(item => predicate(item));
  }

  replaceItem(index: number, item: any) {
    this.data.splice(index, 1, item);
    this.processDataChanges();
  }
}
